/*
 * Copyright (C) 2015 - 2018 Kosmos contact@kosmos.fr
 *
 * Projet: webapp-front
 * Version: 7.4.0-ALPHA-3
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *         http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {handleSwitch} from './search-switch.js';

/**
 * Rafraîchit un élément avec le contenu d'une requête fetch.
 * @param fetchUrl - L'URL de la requête fetch.
 * @param reloadElementId - L'identifiant de l'élément à recharger.
 * @returns {Promise<void>} - Une promesse.
 */
export async function loadAndDisplayFetchResult(fetchUrl, reloadElementId) {
    const response = await fetch(fetchUrl);
    if (!response.ok) {
        console.error('Error: ' + response.status);
    }
    const html = await response.text();
    const reloadElement = document.getElementById(reloadElementId);
    if (reloadElement) {
        reloadElement.innerHTML = html;
        const searchUrl = new URL(fetchUrl);
        const newUrl = new URL(location);
        newUrl.search = searchUrl.search.toString();
        document.dispatchEvent(new CustomEvent('search:refresh', {detail: {url: newUrl}}));
    }
}

/**
 * Rafraîchit un élément à partir des données du formulaire de recherche.
 * @param endpoint - L'URL de l'endpoint.
 * @param elementId - L'identifiant de l'élément à recharger.
 * @returns {Promise<void>} - Une promesse.
 */
export async function refreshElement(endpoint, elementId) {
    // On récupère les données du formulaire de recherche
    const form = getSearchForm();
    if (form) {
        const formData = new FormData(form);
        const location = window.location;
        const urlSearchParams = new URLSearchParams();
        // On ajoute les données du formulaire à l'URL
        formData.forEach((value, key) => {
            urlSearchParams.append(key, value);
        });
        const fetchUrl = new URL(`${endpoint}?${urlSearchParams.toString()}`, location.origin);
        await loadAndDisplayFetchResult(fetchUrl, elementId);
    }
}

/**
 * Rafraîchit un élément à partir des données de l'url.
 * @param endpoint - L'URL de l'endpoint.
 * @param elementId - L'identifiant de l'élément à recharger.
 * @returns {Promise<void>} - Une promesse.
 */
async function refreshElementFromCurrentLocation(endpoint, elementId) {
    // On récupère les données du formulaire de recherche
    const form = getSearchForm();
    if (form) {
        const {search, origin} = window.location;
        const fetchUrl = new URL(`${endpoint}${search.toString()}`, origin);
        await loadAndDisplayFetchResult(fetchUrl, elementId);
    }
}

/**
 * Retourne le formulaire de recherche.
 * @returns {HTMLElement}
 */
export function getSearchForm() {
    return document.querySelector('.js-search-form');
}
/**
 * Récupère l'URL de la recherche.
 * @returns {string} - L'URL de la recherche.
 */
export function getSearchUrl() {
    const form = getSearchForm();
    if (form) {
        return form.getAttribute('data-search-url');
    }
}

/**
 * Récupère l'URL des filtres de recherche.
 * @returns {string} - L'URL des filtres de recherche.
 */
export function getFiltersUrl() {
    const form = getSearchForm();
    if (form) {
        return form.getAttribute('data-search-filters-url');
    }
}

/**
 * Récupère l'URL des résultats de recherche.
 * @returns {string} - L'URL des résultats de recherche.
 */
export function getResultsUrl() {
    const form = getSearchForm();
    if (form) {
        return form.getAttribute('data-search-results-url');
    }
}

/**
 * Récupère l'URL des filtres de recherche.
 * @returns {string} - L'URL des filtres de recherche.
 */
export function getSearchBodyUrl() {
    const form = getSearchForm();
    if (form) {
        return form.getAttribute('data-search-body-url');
    }
}

/**
 * Raffraîchit les résultats de recherche et les filtres.
 */
export async function refreshSearch(elementId) {
    await refreshElement(getSearchBodyUrl(), elementId);
    handleSwitch();
}

/**
 * Raffraîchit les résultats de recherche et les filtres.
 */
export async function refreshSearchFromCurrentLocation(elementId) {
    await refreshElementFromCurrentLocation(getSearchBodyUrl(), elementId);
    handleSwitch();
}

/**
 * Raffraîchit les résultats de recherche.
 */
export async function refreshResults() {
    await refreshElement(getResultsUrl(), 'search-results');
}

/**
 * Rafraîchit les filtres de recherche.
 */
export  async function refreshFilters() {
    await refreshElement(getFiltersUrl(), 'search-bar');
}


/**
 * Désélectionne les sous-agrégations.
 */
export function uncheckSubAggregations(event) {
    event.preventDefault();
    const form = getSearchForm();
    if (form) {
        const sortSelect = form.querySelector('.js-desktop-search-sort');
        if (sortSelect) {
            sortSelect.value = '';
        }
        const inputs = form.querySelectorAll('.js-search-aggregation-panel:not(.js-search-keep-filtering-types) input[type="checkbox"], .js-desktop-search-aggregation:not(.js-search-keep-filtering-types) input[type="checkbox"]');
        inputs.forEach((input) => {
            input.checked = false;
        });
    }
}

const searchBody = document.getElementById('search-body');
if (searchBody && searchBody.dataset.refreshonload === 'true' && window.location.search.match(/[?&]q=/)) {
    await refreshSearchFromCurrentLocation('search-body');
}
